import { useMemo } from 'react';
import type { RunBuildPreset } from '@/shell/RunBuildContext';
import { useApps, useLaunchForm, useProfiles, useProjects, useQueue } from '@/api/hooks';
import {
  availablePlatformsOf,
  environmentChoices,
  groupProfiles,
  isLoadingBasics,
  isRunnerAllowed,
  launchPlatformArg,
  pickApp,
  pickEnvironment,
  pickGroup,
  pickProject,
  primaryProfileOf,
  selectPlatforms,
} from './runBuildModel';
import type { FieldValues } from './useRunBuildFields';

/** What the form targets: the project, app, profile group, environment and platforms the picks resolve to, plus the data behind them. */
export function useRunBuildTarget(preset: RunBuildPreset, v: FieldValues) {
  const projects = useProjects();
  const project = pickProject(projects.data, v.projectId, preset.projectSlug);
  const apps = useApps(project?.id);
  const app = pickApp(apps.data, v.appId);
  const profiles = useProfiles(app?.id);
  const groups = useMemo(() => groupProfiles(profiles.data), [profiles.data]);
  const group = pickGroup(groups, v.profileSlug, preset.profileId);
  const availablePlatforms = useMemo(() => availablePlatformsOf(group), [group]);
  const selectedPlatforms = selectPlatforms(v.platforms, availablePlatforms);
  const environments = useMemo(() => environmentChoices(project?.environments, group), [project, group]);
  const env = pickEnvironment(environments, v.environment);
  const primaryProfile = primaryProfileOf(group, selectedPlatforms, availablePlatforms);
  const launch = useLaunchForm(primaryProfile?.id, env?.slug, launchPlatformArg(selectedPlatforms), v.branch.trim() || undefined);
  const queue = useQueue(project?.id, Boolean(project));
  const loadingBasics = isLoadingBasics([projects, apps, profiles]);
  const runnerAllowed = isRunnerAllowed(group);
  return { projects, project, apps, app, groups, group, availablePlatforms, selectedPlatforms, environments, env, launch, launchData: launch.data, queue, loadingBasics, runnerAllowed };
}

export type RunBuildTarget = ReturnType<typeof useRunBuildTarget>;
